import React, { memo, useEffect, useState } from "react";
import { Box } from "@mui/material";
import useDataSourceManager from "hooks/useDataSourceManager";
import { Loading } from "components/Loading";
import Summary from "./components/Summary";
import DeviceStatusChart from "./components/DeviceStatusChart";
import SsidTrafficChart from "./components/SsidTrafficChart";
import ClientTrafficChart from "./components/ClientTrafficChart";
import RankBarCharts from "./components/RankBarCharts";
import RssiPowerAndTable from "./components/RssiPowerAndTable";
import { getOverviewRequestConfig } from "./helper";
import { v4 as uuidv4 } from "uuid";

const Overview = ({ auth, timeRange }) => {
  const { dataMap, sendBatch } = useDataSourceManager();
  const [requestUuid] = useState(uuidv4());
  const [isLoading, setIsLoading] = useState(true);

  const standalone = !auth.orgs?.id || Object.keys(auth.orgs || {}).length === 0;

  const [overviewData, setOverviewData] = useState({});
  const SummaryData = overviewData.kpi || {};
  const chartsData = overviewData.charts || {};
  const rankings = overviewData.rankings || {};
  const poePowerChanges = Array.isArray(overviewData.poePowerChanges) ? overviewData.poePowerChanges : [];

  // clientStatus
  let clientStatusArray = [];
  if (Array.isArray(chartsData.clientStatus)) {
    clientStatusArray = chartsData.clientStatus;
  } else if (chartsData.clientStatus && Array.isArray(chartsData.clientStatus.online)) {
    const onlineList = chartsData.clientStatus.online;
    const newList = Array.isArray(chartsData.clientStatus.new) ? chartsData.clientStatus.new : [];
    const offlineList = Array.isArray(chartsData.clientStatus.offline) ? chartsData.clientStatus.offline : [];
    clientStatusArray = onlineList.map((onlineVal, idx) => ({
      online: onlineVal ?? 0,
      offline: offlineList[idx] ?? 0,
      new: newList[idx] ?? 0,
    }));
  }

  // wirelessClientRssi
  let rssiArray = [];
  if (Array.isArray(chartsData.wirelessClientRssi)) {
    rssiArray = chartsData.wirelessClientRssi;
  } else if (chartsData.wirelessClientRssi && Array.isArray(chartsData.wirelessClientRssi.weak)) {
    const weakList = chartsData.wirelessClientRssi.weak;
    const fairList = Array.isArray(chartsData.wirelessClientRssi.fair) ? chartsData.wirelessClientRssi.fair : [];
    const goodList = Array.isArray(chartsData.wirelessClientRssi.good) ? chartsData.wirelessClientRssi.good : [];
    rssiArray = weakList.map((weakVal, idx) => ({
      weak: weakVal ?? 0,
      fair: fairList[idx] ?? 0,
      good: goodList[idx] ?? 0,
    }));
  }

  // wirelessTrafficUsage
  let trafficUsageArray = [];
  if (Array.isArray(chartsData.wirelessTrafficUsage)) {
    trafficUsageArray = chartsData.wirelessTrafficUsage.map((item) => ({
      uplink: item?.uplink ?? 0,
      downlink: item?.downlink ?? 0,
    }));
  }

  // ssidTraffic
  let ssidTrafficArray = [];
  if (Array.isArray(chartsData.ssidTraffic)) {
    ssidTrafficArray = chartsData.ssidTraffic.map((item) => ({
      ...item,
      daily: Array.isArray(item?.daily)
        ? item.daily.map((d) => ({
            total: d?.total ?? 0,
            uplink: d?.uplink ?? 0,
            downlink: d?.downlink ?? 0,
          }))
        : [],
    }));
  }

  const sanitizedChartsData = {
    ...chartsData,
    switchStatus: chartsData.switchStatus && typeof chartsData.switchStatus === "object" ? chartsData.switchStatus : {},
    apStatus: chartsData.apStatus && typeof chartsData.apStatus === "object" ? chartsData.apStatus : {},
    clientStatus: clientStatusArray,
    wirelessTrafficUsage: trafficUsageArray,
    ssidTraffic: ssidTrafficArray,
    wirelessClientRssi: rssiArray,
    poePowerConsumption: chartsData.poePowerConsumption && typeof chartsData.poePowerConsumption === "object" ? chartsData.poePowerConsumption : {},
  };

  const getOverviewData = async (apiResource, restfulPlaceholders) => {
    setIsLoading(true);
    try {
      const { getSendBatchData } = await sendBatch([
        { apiKey: apiResource, requestUuid, restfulPlaceholders, params: {} }
      ]);
      const res = getSendBatchData({ requestUuid }) || {};
      setOverviewData(res?.data || {});
    } catch (err) {
      console.error("Failed to load summary report overview data", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const summaryReportId = timeRange?.summaryReportId;
    if (!summaryReportId) {
      setIsLoading(false);
      return;
    }

    const { apiResource, restfulPlaceholders } = getOverviewRequestConfig(
      auth,
      summaryReportId,
      standalone
    );

    getOverviewData(apiResource, restfulPlaceholders);
  }, [timeRange?.summaryReportId, requestUuid, standalone, auth.realm?.id, auth.orgs?.id, auth.sites?.id]);

  return (
    <Box sx={{ position: "relative", px: "20px", pb: "30px", minHeight: "300px" }}>
      {isLoading && <Loading />}

      {!isLoading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Summary */}
          <Summary SummaryData={SummaryData} />

          {/* Switch & AP online / offline */}
          <DeviceStatusChart chartsData={sanitizedChartsData} />

          {/* SSID by Traffic */}
          <SsidTrafficChart ssidTraffic={sanitizedChartsData.ssidTraffic} />

          {/* Clients & Traffic Usage */}
          <ClientTrafficChart chartsData={sanitizedChartsData} />

          {/* Top 5 rankings horizontal bars */}
          <RankBarCharts rankings={rankings} />

          {/* RSSI, PoE charts and changes table */}
          <RssiPowerAndTable chartsData={sanitizedChartsData} poePowerChanges={poePowerChanges} />
        </Box>
      )}
    </Box>
  );
};

export default memo(Overview);
